import React, { useState, useEffect, useRef, useCallback } from "react";
import { motion } from "framer-motion";
import { Check, ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import "./WorkedWithVelocity.scss";

import lKvs      from "../assets/partners/l-kvs.webp";
import lJnv      from "../assets/partners/l-jnv_11zon.webp";
import lPmshri   from "../assets/partners/l-pmshree_11zon.webp";
import lScert    from "../assets/partners/l-scert_11zon.webp";
import lUkEd     from "../assets/partners/l-ukEd.webp";
import lDoSW     from "../assets/partners/l-DoSW.webp";
import lDbse     from "../assets/partners/l-dbse_11zon.webp";
import lNasscom  from "../assets/partners/l-nasscom.webp";
import lFitt     from "../assets/partners/l-fitt-iid_11zon.webp";
import lIhfc     from "../assets/partners/l-ihfc.webp";
import lThinq    from "../assets/partners/l-thingqbator.webp";
import lSiemt    from "../assets/partners/l-siemt_11zon.webp";
import lAdobe    from "../assets/partners/l-adobe.webp";
import lNests    from "../assets/incubation/logo-emrs.jpg";
import lNielit   from "../assets/partners/l-nielit.jpg";
import lIiitdIic from "../assets/partners/l-iiitd-dic.png";

const PARTNERS = [
  { id: 1,  image: lIiitdIic, name: "IIITD Innovation & Incubation Center", sub: "IIIT Delhi",                        desc: "CoGrad is incubated at IIIT Delhi's Innovation & Incubation Center, building next-generation AI solutions for education." },
  { id: 2,  image: lKvs,     name: "Kendriya Vidyalaya Sangathan",     sub: "Central Government",                        desc: "Supporting holistic development and excellence in school education across India." },
  { id: 3,  image: lJnv,     name: "Navodaya Vidyalaya Samiti",        sub: "Central Government",                        desc: "Empowering rural students through quality education and equal opportunities nationwide." },
  { id: 4,  image: lPmshri,  name: "PM SHRI Schools",                  sub: "PM Schools for Rising India",               desc: "Building model schools with modern infrastructure aligned with NEP 2020 vision." },
  { id: 5,  image: lScert,   name: "SCERT Uttarakhand",                sub: "State Educational Research",                desc: "Strengthening teacher training and curriculum development in Uttarakhand." },
  { id: 6,  image: lUkEd,    name: "Uttarakhand Education",            sub: "State Government",                          desc: "Partnering on state-wide educational transformation and development initiatives." },
  { id: 7,  image: lDoSW,    name: "Dept. of Social Welfare",          sub: "Uttar Pradesh Govt.",                       desc: "Working together for inclusive education and holistic community development." },
  { id: 8,  image: lDbse,    name: "Delhi Board of School Education",  sub: "DBSE — Delhi Govt.",                        desc: "Assessing learning outcomes and enhancing academic standards in Delhi schools." },
  { id: 9,  image: lNasscom, name: "NASSCOM Foundation",               sub: "Tech for Change",                           desc: "Collaborating on digital literacy and technology-driven learning programmes." },
  { id: 10, image: lFitt,    name: "FITT — IIT Delhi",                 sub: "Foundation for Innovation",                 desc: "Driving innovation and technology transfer to build future-ready education solutions." },
  { id: 11, image: lIhfc,    name: "IHFC",                             sub: "India HCI Foundation",                      desc: "Fostering human-computer interaction research and innovation in education." },
  { id: 12, image: lThinq,   name: "ThinkQbator",                      sub: "Startup Incubator",                         desc: "Incubating education startups and fostering entrepreneurial thinking in students." },
  { id: 13, image: lSiemt,   name: "SIEMT",                            sub: "Skill Development",                         desc: "Building industry-aligned skill development programmes for the workforce of tomorrow." },
  { id: 14, image: lAdobe,   name: "Adobe",                            sub: "Technology Partner",                        desc: "Bringing creative technology tools into classrooms to inspire student creativity." },
  { id: 15, image: lNests,   name: "NESTS — EMRS",                     sub: "Ministry of Tribal Affairs",                desc: "Signed MoU to strengthen AI-enabled academic support across Eklavya Model Residential Schools." },
  { id: 16, image: lNielit,  name: "NIELIT",                           sub: "MeitY, Govt. of India",                     desc: "Collaborating on digital literacy and IT skill development programmes under the National Institute of Electronics & IT." },
];

const HALF = 2;

const scaleMap   = [0.82, 0.93, 1.07, 0.93, 0.82];
const opacityMap = [0.42, 0.70, 1.00, 0.70, 0.42];

export default function WorkedWithVelocity() {
  const [activeIndex, setActiveIndex] = useState(0);
  const intervalRef = useRef(null);
  const touchStartX = useRef(null);
  const sectionRef = useRef(null);
  const hasStarted = useRef(false);

  const startAutoPlay = useCallback(() => {
    clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setActiveIndex(prev => (prev + 1) % PARTNERS.length);
    }, 8000);
  }, []);

  // Hold on the first partner until the section is actually on screen — on
  // mobile only the centre card is visible, so autoplaying from page load
  // means the opening cards are gone before anyone scrolls this far down.
  useEffect(() => {
    const node = sectionRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          if (!hasStarted.current) {
            hasStarted.current = true;
            setActiveIndex(0);
          }
          startAutoPlay();
        } else {
          clearInterval(intervalRef.current);
        }
      },
      { threshold: 0.35 }
    );

    observer.observe(node);
    return () => {
      observer.disconnect();
      clearInterval(intervalRef.current);
    };
  }, [startAutoPlay]);

  const goNext = useCallback(() => {
    setActiveIndex(prev => (prev + 1) % PARTNERS.length);
    startAutoPlay();
  }, [startAutoPlay]);

  const goPrev = useCallback(() => {
    setActiveIndex(prev => (prev - 1 + PARTNERS.length) % PARTNERS.length);
    startAutoPlay();
  }, [startAutoPlay]);

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 40) diff > 0 ? goNext() : goPrev();
    touchStartX.current = null;
  };

  const visibleItems = Array.from({ length: HALF * 2 + 1 }, (_, i) => {
    const idx = ((activeIndex + (i - HALF)) % PARTNERS.length + PARTNERS.length) % PARTNERS.length;
    return { partner: PARTNERS[idx], pos: i };
  });

  return (
    <section className="wwv-wrap" ref={sectionRef}>
      <div className="wwv-inner">

        <motion.div
          className="wwv__top"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.4, ease: "easeOut" }}
        >
          <div className="wwv__chip">✦ OUR PARTNERS</div>
          <h2 className="wwv__h2">We Have <span>Worked With</span></h2>
          <div className="wwv__rule" />
          <p className="wwv__sub">
            Collaborating with government bodies and leading organizations<br />
            to create meaningful impact in education.
          </p>
        </motion.div>

        <div className="wwv__carousel-wrap">
          <button className="wwv__nav wwv__nav--prev" onClick={goPrev} aria-label="Previous">
            <ChevronLeft size={20} />
          </button>

          <div
            className="wwv__carousel"
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
          >
            <div className="wwv__track">
              {visibleItems.map(({ partner: p, pos }) => (
                <motion.div
                  key={pos}
                  className="wwv__card"
                  animate={{
                    scale:   scaleMap[pos],
                    opacity: opacityMap[pos],
                  }}
                  transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                >
                  <span className="wwv__check">
                    <Check size={13} strokeWidth={3} />
                  </span>

                  <div className="wwv__logo">
                    <img src={p.image} alt={p.name} />
                  </div>

                  <div className="wwv__body">
                    <h3 className="wwv__name">{p.name}</h3>
                    <p className="wwv__cat">{p.sub}</p>
                    <div className="wwv__dash" />
                    <p className="wwv__desc">{p.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          <button className="wwv__nav wwv__nav--next" onClick={goNext} aria-label="Next">
            <ChevronRight size={20} />
          </button>
        </div>

        <div className="wwv__cta">
          <a href="/contact-us" className="wwv__btn">
            Become a Partner <ArrowRight size={15} />
          </a>
        </div>

      </div>
    </section>
  );
}
